import type { ParticipantDocument } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import type { Editor } from '@tiptap/react';
import React from 'react';
import { FaClock, FaListOl } from 'react-icons/fa';
import { EditorOutlineSection } from '../../components/tiptap/EditorOutlineSection';
import { getCommentThreads } from '../../components/tiptap/extensions/comment/Comment';
import { useEditorState } from '../../components/tiptap/hooks/useEditorState';
import { ParticipantDocumentReviewSectionMemo } from './ParticipantDocumentReviewSection';

interface Props {
  participantDocument: ParticipantDocument;
  editor: Editor | null;
}

export const ParticipantDocumentSidebar = (props: Props) => {
  const { participantDocument, editor } = props;

  const editorState = useEditorState({
    editor,
    selector: (state) => {
      return {
        commentThreads: state.editor?.storage?.comment?.document
          ? getCommentThreads(state.editor.storage.comment.document)
          : [],
      };
    },
    equalityFn: P.isEqual,
  });

  if (!editor || !editorState) {
    return null;
  }

  const { commentThreads } = editorState;

  return (
    <>
      <ChakraV3.Tabs.Root
        size='sm'
        variant='subtle'
        w='full'
        h='full'
        display='flex'
        flexDir='column'
        defaultValue='review'
      >
        <ChakraV3.Tabs.List px={3}>
          <ChakraV3.Tabs.Trigger fontSize='xs' value='review' w='full'>
            <ChakraV3.HStack w='full' justify='center'>
              <FaClock />
              Review
            </ChakraV3.HStack>
          </ChakraV3.Tabs.Trigger>
          <ChakraV3.Tabs.Trigger fontSize='xs' value='outline' w='full'>
            <ChakraV3.HStack w='full' justify='center'>
              <FaListOl />
              Outline
            </ChakraV3.HStack>
          </ChakraV3.Tabs.Trigger>
        </ChakraV3.Tabs.List>

        <ChakraV3.Tabs.ContentGroup overflowY='auto' h='full'>
          <ChakraV3.Tabs.Content h='full' value='review'>
            <ParticipantDocumentReviewSectionMemo
              participantDocument={participantDocument}
              commentThreads={commentThreads}
              editor={editor}
            />
          </ChakraV3.Tabs.Content>
          <ChakraV3.Tabs.Content h='full' w='full' value='outline'>
            <EditorOutlineSection editor={editor} />
          </ChakraV3.Tabs.Content>
        </ChakraV3.Tabs.ContentGroup>
      </ChakraV3.Tabs.Root>
    </>
  );
};
